import { useState, useEffect } from 'react';

interface PositionSliderProps {
  value: number | null;
  onChange: (value: number) => void;
  disabled?: boolean;
}

const POSITION_RANGES = [
  { min: 0, max: 20, label: 'Centre Back/Fullback', color: 'bg-blue-700', textColor: 'text-blue-700' },
  { min: 21, max: 40, label: 'Defensive Mid', color: 'bg-blue-500', textColor: 'text-blue-500' },
  { min: 41, max: 60, label: 'Central Mid', color: 'bg-green-500', textColor: 'text-green-500' },
  { min: 61, max: 80, label: 'Attacking Mid', color: 'bg-orange-500', textColor: 'text-orange-500' },
  { min: 81, max: 100, label: 'Striker/Winger', color: 'bg-red-500', textColor: 'text-red-500' },
];

const EMPHASIZED_ATTRIBUTES: Record<string, string> = {
  '0-20': 'Tackling, Marking, Positioning',
  '21-40': 'Work Rate, Stamina, Decision Making',
  '41-60': 'Passing, Decision Making, Work Rate',
  '61-80': 'Passing, Off the Ball, Positioning',
  '81-100': 'Finishing, Pace, Off the Ball',
};

function getPositionInfo(value: number) {
  const range = POSITION_RANGES.find(r => value >= r.min && value <= r.max);
  const attributeKey = Object.keys(EMPHASIZED_ATTRIBUTES).find(key => {
    const [min, max] = key.split('-').map(Number);
    return value >= min && value <= max;
  });

  return {
    label: range?.label || 'Central Mid',
    color: range?.color || 'bg-green-500',
    textColor: range?.textColor || 'text-green-500',
    attributes: attributeKey ? EMPHASIZED_ATTRIBUTES[attributeKey] : '',
  };
}

export default function PositionSlider({ value, onChange, disabled = false }: PositionSliderProps) {
  const [localValue, setLocalValue] = useState(value ?? 50);
  const positionInfo = getPositionInfo(localValue);

  useEffect(() => {
    if (value !== null) {
      setLocalValue(value);
    }
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(e.target.value);
    setLocalValue(newValue);
    onChange(newValue);
  };

  return (
    <div className="space-y-4">
      {/* BETA Badge */}
      <div className="flex items-center gap-2">
        <span className="px-2 py-1 text-xs font-semibold bg-purple-100 text-purple-700 rounded">
          BETA
        </span>
        <span className="text-sm text-gray-600 dark:text-gray-400">
          Position Preference Slider
        </span>
      </div>

      {/* Current Position Label */}
      <div className="text-center">
        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg ${positionInfo.color} text-white`}>
          <span className="font-semibold">{positionInfo.label}</span>
          <span className="text-sm opacity-90">({localValue})</span>
        </div>
      </div>

      {/* Slider */}
      <div className="relative pt-6 pb-2">
        {/* Position markers */}
        <div className="absolute top-0 left-0 right-0 flex justify-between text-xs text-gray-500 dark:text-gray-400">
          <span>Defender</span>
          <span>Midfielder</span>
          <span>Striker</span>
        </div>

        {/* Color gradient track */}
        <div className="absolute top-8 left-0 right-0 h-2 rounded-full" style={{
          background: 'linear-gradient(to right, #1e40af 0%, #3b82f6 25%, #22c55e 50%, #f97316 75%, #ef4444 100%)'
        }} />

        {/* Slider input */}
        <input
          type="range"
          min="0"
          max="100"
          value={localValue}
          onChange={handleChange}
          disabled={disabled}
          className="relative w-full h-2 bg-transparent appearance-none cursor-pointer slider-thumb"
          style={{ zIndex: 10 }}
        />

        {/* Value markers */}
        <div className="flex justify-between mt-1 text-xs text-gray-400">
          <span>0</span>
          <span>25</span>
          <span>50</span>
          <span>75</span>
          <span>100</span>
        </div>
      </div>

      {/* Emphasized Attributes */}
      <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
          Emphasized Attributes
        </div>
        <div className="text-sm text-gray-700 dark:text-gray-300">
          {positionInfo.attributes}
        </div>
      </div>

      {/* Info Text */}
      <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
        <p>
          • The slider determines your positional play style from defensive to attacking
        </p>
        <p>
          • Your effective rating is calculated based on your position preference
        </p>
        <p>
          • Each position emphasizes different attributes in team generation
        </p>
      </div>

      {/* Custom slider styles */}
      <style>{`
        .slider-thumb::-webkit-slider-thumb {
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: white;
          border: 3px solid ${positionInfo.color.replace('bg-', '#')};
          cursor: pointer;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }

        .slider-thumb::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: white;
          border: 3px solid ${positionInfo.color.replace('bg-', '#')};
          cursor: pointer;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }

        .slider-thumb:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
}
